import { ISignCommand } from '@/sign/commands/isign.command';
import { SigningParams, SigningParamsContainer } from '@/sign/models/signing-params.model';
import { ISignStrategyFactory } from '../factories/isign-strategy.factory';
import { PrivateKeyType, SignMethod } from '../models/sign-constants';
import { ISignStrategy } from '../strategies/isign.strategy';
import { FileSigningResult } from '@/models/document-sign.models';
import { AppError } from '@/models/log.models';
import { getLocale } from '@/helpers';
import { showAlert } from '@/plugins/dialogs';
import { wait } from '@/helpers/ui-helper';
import { throwCancelled } from '@/helpers/error-helper';

export abstract class BaseSignDocumentCommand implements ISignCommand {
  protected signStrategyParams: any;
  protected factory: ISignStrategyFactory;
  protected isSealAllowed: boolean = false;

  private signMethodParams: SigningParamsContainer = {} as SigningParamsContainer;

  protected constructor(signStrategyParams: any, factory: ISignStrategyFactory) {
    if (!signStrategyParams) throw new Error('Strategy signing params are not defined');
    if (!factory) throw new Error('Factory is not defined');
    this.signStrategyParams = signStrategyParams;
    this.factory = factory;
  }

  protected get KeySigningParams(): SigningParams {
    return this.signMethodParams[PrivateKeyType.Key];
  }

  protected get KeySignMethod(): SignMethod {
    return this.KeySigningParams.signMethod as SignMethod;
  }

  protected get IsSealUsed(): boolean {
    return PrivateKeyType.Seal in this.signMethodParams;
  }

  protected get SealSigningParams(): SigningParams {
    return this.signMethodParams[PrivateKeyType.Seal];
  }

  protected get SealSignMethod(): SignMethod {
    return this.SealSigningParams.signMethod as SignMethod;
  }

  public async execute(signMethodParams: SigningParamsContainer): Promise<any> {
    this.signMethodParams = signMethodParams;

    this.validate();

    let keySigningResult: Array<FileSigningResult> = [];
    let sealSigningResult: Array<FileSigningResult> | null = null;
    try {
      keySigningResult = await this.getStrategy(this.KeySigningParams).execute(this.KeySigningParams) as Array<FileSigningResult>;
    } catch (e) {
      e['errorSource'] = PrivateKeyType.Key;
      throw e;
    }

    try {
      sealSigningResult = this.IsSealUsed
        ? await this.getStrategy(this.SealSigningParams, keySigningResult).execute(this.SealSigningParams) as Array<FileSigningResult>
        : null;
    } catch (e) {
      e['errorSource'] = PrivateKeyType.Seal;
      throw e;
    }

    const result = { keySigningResult, sealSigningResult } as ICommandSigningResult;

    this._prepareSignResults(result);

    return result;
  }

  private validate(): void {
    if (!this.signMethodParams) throw new Error('Signing params is not defined');

    if (!this.KeySigningParams) throw new Error('Key signing params is not defined');

    if (this.IsSealUsed) {
      if (!this.isSealAllowed) throw new Error('Using seal is not allowed');

      if (!this.SealSigningParams) throw new Error('Seal signing params is not defined');
    }
  }

  private getStrategy(signingParams: SigningParams | null, previousSigningResults?: Array<FileSigningResult>): ISignStrategy {
    const signStrategyParams = this.signStrategyParams || {};
    signStrategyParams.previousSigningResults = previousSigningResults;

    switch (signingParams?.signMethod) {
      case SignMethod.FileSystem:
        return this.factory.createSignFileSystemStrategy(this.signStrategyParams);
      case SignMethod.Device:
        return this.factory.createSignDeviceStrategy(this.signStrategyParams);
      case SignMethod.Cloud:
        return this.factory.createSignCloudStrategy(signingParams.signProvider, signingParams.keyReaderType, this.signStrategyParams);
      default:
        throw new Error('Sign method is not supported');
    }
  }

  protected async handleSignSavingError(err: any): Promise<void> {
    if (err instanceof AppError && err.isInternalNestedSignatureChangedDuringSigningError) {
      let fileName = err.details.length > 0 ? err.details[0].key : '';
      const msg = getLocale('appSign.warnings.internalNestedSignChangeDuringSigning', { fileName: fileName }).toString();

      showAlert(msg, '', true);

      await wait(100);

      throwCancelled();
    } else {
      throw err;
    }
  }

  private _prepareSignResults(result: ICommandSigningResult): void {
    if (!result.sealSigningResult || !result.sealSigningResult.length) return;

    for (const keySigningResult of result.keySigningResult) {
      if (!keySigningResult.isInternalNested) continue;

      let sealSigningResult = result.sealSigningResult
        .find(sr => sr.isInternalNested && sr.pDocumentVersionsFileCode === keySigningResult.pDocumentVersionsFileCode);

      if (sealSigningResult) {
        keySigningResult.pSignBase64 = '';
      }
    }
  }
}

export interface ICommandSigningResult {
  keySigningResult: Array<FileSigningResult>;
  sealSigningResult: Array<FileSigningResult> | null;
}
